/**
 * L'histoire d'un fournisseur — les appels (V1.6-11).
 *
 * Trois règles tenues ici :
 *
 * 1. **lecture seule.** Aucune mutation n'existe dans ce module. Consulter une
 *    métrique n'écrit rien, et un écran d'analyse qui expose un bouton finit
 *    par en devenir un ;
 * 2. **l'écran ne recalcule aucune métrique.** Taux, délais, médianes arrivent
 *    calculés, avec leur échantillon et leur état. Refaire une division côté
 *    client donnerait un deuxième chiffre, et le jour où les deux divergent
 *    personne ne saurait lequel croire ;
 * 3. **inconnu n'est pas zéro.** `status` décide ce qui s'affiche, jamais
 *    `value ?? 0`.
 *
 * `staleTime` plus généreux qu'ailleurs : ces chiffres décrivent douze mois
 * d'histoire et ne changent pas d'une seconde à l'autre. Une fiche de commande
 * doit être fraîche ; une moyenne de délai sur un an, non.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  PriceObservation,
  ProductSupplierHistory,
  Supplier,
  SupplierComparison,
  SupplierIntelligence,
  Uuid,
} from "./types";

/** Cinq minutes : l'histoire d'un an ne bouge pas pendant qu'on la lit. */
const HISTORY_STALE_MS = 5 * 60 * 1000;

export const intelligenceKeys = {
  all: ["supplier-intelligence"] as const,
  supplier: (supplierId: Uuid) => ["supplier-intelligence", "supplier", supplierId] as const,
  metrics: (supplierId: Uuid, params: object) =>
    ["supplier-intelligence", supplierId, "metrics", params] as const,
  prices: (supplierId: Uuid, productId: Uuid | undefined) =>
    ["supplier-intelligence", supplierId, "prices", productId ?? "all"] as const,
  product: (supplierId: Uuid, productId: Uuid) =>
    ["supplier-intelligence", supplierId, "product", productId] as const,
  comparison: (params: object) => ["supplier-intelligence", "comparison", params] as const,
};

export interface PeriodParams {
  [key: string]: unknown;
  date_from?: string;
  date_to?: string;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function useSupplier(supplierId: Uuid | undefined) {
  return useQuery({
    queryKey: intelligenceKeys.supplier(supplierId ?? ""),
    queryFn: () => apiFetch<Supplier>(`/suppliers/${supplierId}`),
    enabled: Boolean(supplierId),
  });
}

export function useSupplierIntelligence(supplierId: Uuid | undefined, params: PeriodParams = {}) {
  return useQuery({
    queryKey: intelligenceKeys.metrics(supplierId ?? "", params),
    queryFn: () =>
      apiFetch<SupplierIntelligence>(query(`/suppliers/${supplierId}/intelligence`, params)),
    enabled: Boolean(supplierId),
    staleTime: HISTORY_STALE_MS,
  });
}

export function useSupplierPriceHistory(supplierId: Uuid | undefined, productId?: Uuid) {
  return useQuery({
    queryKey: intelligenceKeys.prices(supplierId ?? "", productId),
    queryFn: () =>
      apiFetch<PriceObservation[]>(
        query(`/suppliers/${supplierId}/price-history`, { product_id: productId }),
      ),
    enabled: Boolean(supplierId),
    staleTime: HISTORY_STALE_MS,
  });
}

export function useProductSupplierHistory(
  supplierId: Uuid | undefined,
  productId: Uuid | undefined,
) {
  return useQuery({
    queryKey: intelligenceKeys.product(supplierId ?? "", productId ?? ""),
    queryFn: () =>
      apiFetch<ProductSupplierHistory>(
        `/suppliers/${supplierId}/products/${productId}/history`,
      ),
    enabled: Boolean(supplierId) && Boolean(productId),
    staleTime: HISTORY_STALE_MS,
  });
}

export function useSupplierComparison(params: PeriodParams = {}) {
  return useQuery({
    queryKey: intelligenceKeys.comparison(params),
    queryFn: () => apiFetch<SupplierComparison>(query("/supplier-comparison", params)),
    staleTime: HISTORY_STALE_MS,
  });
}
